import { tapEffect, tapMemo, tapRef, tapResource, tapInlineResource, } from "@assistant-ui/tap";
import { tapClientStack, tapWithClientStack, SYMBOL_CLIENT_INDEX, } from "./utils/tap-client-stack-context.js";
import { BaseProxyHandler, handleIntrospectionProp, } from "./utils/BaseProxyHandler.js";
import { wrapperResource } from "./wrapperResource.js";
/**
 * Symbol used internally to get state from ClientProxy.
 * This allows getState() to be optional in the user-facing client.
 */
const SYMBOL_GET_OUTPUT = Symbol("assistant-ui.store.getValue");
export const getClientState = (client) => {
    const output = client[SYMBOL_GET_OUTPUT];
    if (!output) {
        throw new Error("Client scope contains a non-client resource. " +
            "Ensure your Derived get() returns a client created with tapClientResource(), not a plain resource.");
    }
    return output.state;
};
// Global cache for function templates by field name
const fieldAccessFns = new Map();
function getOrCreateProxyFn(prop) {
    let template = fieldAccessFns.get(prop);
    if (!template) {
        template = function (...args) {
            if (!this || typeof this !== "object") {
                throw new Error(`Method "${String(prop)}" called without proper context. ` +
                    `This may indicate the function was called incorrectly.`);
            }
            const output = this[SYMBOL_GET_OUTPUT];
            if (!output) {
                throw new Error(`Method "${String(prop)}" called on invalid client proxy. ` +
                    `Ensure you are calling this method on a valid client instance.`);
            }
            const method = output.methods[prop];
            if (!method)
                throw new Error(`Method "${String(prop)}" is not implemented.`);
            if (typeof method !== "function")
                throw new Error(`"${String(prop)}" is not a function.`);
            return method(...args);
        };
        fieldAccessFns.set(prop, template);
    }
    return template;
}
class ClientProxyHandler extends BaseProxyHandler {
    outputRef;
    index;
    boundFns;
    cachedReceiver;
    constructor(outputRef, index) {
        super();
        this.outputRef = outputRef;
        this.index = index;
    }
    get(_, prop, receiver) {
        if (prop === SYMBOL_GET_OUTPUT)
            return this.outputRef.current;
        if (prop === SYMBOL_CLIENT_INDEX)
            return this.index;
        const introspection = handleIntrospectionProp(prop, "ClientProxy");
        if (introspection !== false)
            return introspection;
        const value = this.outputRef.current.methods[prop];
        if (typeof value === "function") {
            if (this.cachedReceiver !== receiver) {
                this.boundFns = new Map();
                this.cachedReceiver = receiver;
            }
            let bound = this.boundFns.get(prop);
            if (!bound) {
                bound = getOrCreateProxyFn(prop).bind(receiver);
                this.boundFns.set(prop, bound);
            }
            return bound;
        }
        return value;
    }
    ownKeys() {
        return Object.keys(this.outputRef.current.methods);
    }
    has(_, prop) {
        if (prop === SYMBOL_GET_OUTPUT)
            return true;
        if (prop === SYMBOL_CLIENT_INDEX)
            return true;
        return prop in this.outputRef.current.methods;
    }
}
/**
 * Resource that wraps a plain resource element to create a stable client proxy.
 *
 * Takes a ResourceElement that returns { state, methods } and
 * wraps it to produce a stable client proxy. This adds the client to the
 * client stack, enabling event scoping.
 *
 * Use this for 1:1 client mappings where you want event scoping to work correctly.
 *
 * @example
 * ```typescript
 * const MessageResource = resource(({ messageId }: { messageId: string }) => {
 *   return tapInlineResource(
 *     tapClientResource(InnerMessageResource({ messageId }))
 *   );
 * });
 * ```
 */
export const ClientResource = wrapperResource((element) => {
    const valueRef = tapRef(null);
    const index = tapClientStack().length;
    const methods = tapMemo(() => new Proxy({}, new ClientProxyHandler(valueRef, index)), [index]);
    const value = tapWithClientStack(methods, () => tapResource(element));
    if (!valueRef.current) {
        valueRef.current = value;
    }
    tapEffect(() => {
        valueRef.current = value;
    });
    return { methods, state: value.state, key: element.key };
});
export const tapClientResource = (element) => {
    return tapInlineResource(ClientResource(element));
};
//# sourceMappingURL=tapClientResource.js.map